import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../../util/Toast';
import { useSettings } from '../util/SettingsProvider';
export const Reports = () => {
  const [reportData, setReportData] = useState({
    totalClicks: 0,
    totalConv: 0,
    totalEarn: '0.00',
    offers: []
  });
  const [selectedDate, setSelectedDate] = useState('Today');
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  // Helper function to derive YYYY-MM-DD date ranges for backend query
  const getDateRange = (filter) => {
    const today = new Date();
    const formatDate = (d) => d.toISOString().split('T')[0];

    let fromDate = formatDate(today);
    let toDate = formatDate(today);

    if (filter === 'Yesterday') {
      const yest = new Date(today);
      yest.setDate(today.getDate() - 1);
      fromDate = formatDate(yest);
      toDate = formatDate(yest);
    } else if (filter === 'Last 7 Days') {
      const past = new Date(today);
      past.setDate(today.getDate() - 6);
      fromDate = formatDate(past);
      toDate = formatDate(today);
    } else if (filter === 'This Month') {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      fromDate = formatDate(firstDay);
      toDate = formatDate(today);
    }

    return { fromDate, toDate };
  };
  const settings = useSettings();
  useEffect(() => { document.title = `Reports | ${settings?.settings?.site_name || ''}`; }, [settings?.settings?.site_name]);
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  useEffect(() => {
    const fetchReports = async () => {
      const token = getCookie('authToken');
      try {
        setLoading(true);
        const baseUrl = process.env.REACT_APP_API_URL || '';
        const { fromDate, toDate } = getDateRange(selectedDate);

        // Correctly passing parameters using the 'params' property for a GET request
        const response = await axios.get(`${baseUrl}/api/report`, {
          params: {
            fromDate,
            toDate
          },
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data && response.data.data) {
          const apiData = response.data.data;
          setReportData({
            totalClicks: Number(apiData.totalClicks || 0),
            totalConv: Number(apiData.totalConv || 0),
            totalEarn: apiData.totalEarn || '0.00',
            offers: Array.isArray(apiData.offers) ? apiData.offers : []
          });
        }
      } catch (error) {
        console.error('Error fetching reports:', error);
        toast.error(error.response?.data?.msg || "An unexpected error occurred.");
        if (error.response && error.response.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, [selectedDate, navigate]);
  // Helper to safely render image URLs vs fallback emojis
  const renderLogo = (logo, name) => {
    const isUrl = typeof logo === 'string' && (logo.startsWith('http') || logo.startsWith('/'));

    if (isUrl) {
      return (
        <img
          src={logo}
          alt={name || 'Offer'}
          loading="lazy"
          decoding="async"
          className="w-14 h-14 bg-zinc-950 rounded-2xl object-contain p-1 border border-zinc-800 shadow-inner group-hover:scale-110 group-hover:border-emerald-500/30 transition-all duration-300"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(name || 'Offer') + '&background=059669&color=fff';
          }}
        />
      );
    }

    return (
      <div className="w-14 h-14 bg-zinc-950 rounded-2xl flex items-center justify-center text-2xl border border-zinc-800 shadow-inner group-hover:scale-110 group-hover:border-emerald-500/30 transition-all duration-300">
        {logo || '🎯'}
      </div>
    );
  };

  return (
    <div className="w-full pt-16 sm:pt-24 lg:pt-28 pb-16 px-4 sm:px-8 lg:pl-80 relative overflow-hidden font-sans">
      <style>{`
        @keyframes springIn {
          0% { opacity: 0; transform: translateY(16px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes pulseGlow {
          0%, 100% { opacity: 0.15; transform: scale(1); }
          50% { opacity: 0.35; transform: scale(1.08); }
        }

        .animate-spring-in {
          animation: springIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .ambient-glow {
          animation: pulseGlow 8s ease-in-out infinite;
        }

        .micro-interaction {
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .micro-interaction:hover {
          transform: translateY(-2px);
        }

        .micro-interaction:active {
          transform: translateY(0px) scale(0.98);
        }
      `}</style>

      {/* Fluid ambient background lighting */}
      <div className="absolute top-10 left-1/3 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none ambient-glow" />
      <div className="absolute bottom-10 right-10 w-80 h-80 bg-teal-500/10 rounded-full blur-[100px] pointer-events-none ambient-glow" />

      <div className="max-w-[1600px] mx-auto space-y-8 relative z-10">

        {/* Header Banner & Date Filter */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 bg-zinc-900/80 backdrop-blur-md p-6 rounded-[2.5rem] border border-zinc-800/80 shadow-2xl animate-spring-in">
          <div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-100">
              Performance Reports
            </h2>
            <p className="text-zinc-400 text-sm mt-1 font-medium">
              Analyze your traffic and conversion data.
            </p>
          </div>

          <div className="relative inline-block w-full sm:w-auto">
            <button
              onClick={() => {
                const dates = ['Today', 'Yesterday', 'Last 7 Days', 'This Month'];
                const nextIndex = (dates.indexOf(selectedDate) + 1) % dates.length;
                setSelectedDate(dates[nextIndex]);
              }}
              className="w-full sm:w-auto flex items-center justify-between gap-4 px-6 py-3.5 bg-zinc-950/80 border border-zinc-800 rounded-2xl shadow-lg hover:border-emerald-500/50 transition-all group micro-interaction"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/10 text-emerald-400 rounded-xl group-hover:bg-emerald-500 group-hover:text-zinc-950 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="font-bold text-zinc-200 text-sm">{selectedDate}</span>
              </div>
              <svg className="w-4 h-4 text-zinc-500 group-hover:text-emerald-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Top Summary Metrics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-spring-in">

          {/* Total Clicks */}
          <div className="bg-zinc-900/80 backdrop-blur-md p-6 rounded-[2rem] border border-zinc-800/80 shadow-xl hover:border-emerald-500/30 transition-all flex items-center gap-5 micro-interaction">
            <div className="bg-emerald-500/10 text-emerald-400 w-14 h-14 rounded-2xl flex-shrink-0 flex items-center justify-center border border-emerald-500/20">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
              </svg>
            </div>
            <div className="text-left">
              <p className="text-zinc-500 text-[10px] font-black uppercase tracking-widest leading-none mb-1">Total Clicks</p>
              <h3 className="text-2xl font-black tracking-tighter text-zinc-100 leading-tight">
                {loading ? '...' : reportData.totalClicks}
              </h3>
            </div>
          </div>

          {/* Conversions */}
          <div className="bg-zinc-900/80 backdrop-blur-md p-6 rounded-[2rem] border border-zinc-800/80 shadow-xl hover:border-amber-500/30 transition-all flex items-center gap-5 micro-interaction">
            <div className="bg-amber-500/10 text-amber-400 w-14 h-14 rounded-2xl flex-shrink-0 flex items-center justify-center border border-amber-500/20">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <div className="text-left">
              <p className="text-zinc-500 text-[10px] font-black uppercase tracking-widest leading-none mb-1">Conversions</p>
              <h3 className="text-2xl font-black tracking-tighter text-zinc-100 leading-tight">
                {loading ? '...' : reportData.totalConv}
              </h3>
            </div>
          </div>

          {/* Total Earnings */}
          <div className="bg-zinc-900/80 backdrop-blur-md p-6 rounded-[2rem] border border-zinc-800/80 shadow-xl hover:border-emerald-500/30 transition-all flex items-center gap-5 micro-interaction sm:col-span-2 lg:col-span-1">
            <div className="bg-emerald-500/10 text-emerald-400 w-14 h-14 rounded-2xl flex-shrink-0 flex items-center justify-center border border-emerald-500/20">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="text-left">
              <p className="text-zinc-500 text-[10px] font-black uppercase tracking-widest leading-none mb-1">Total Earnings</p>
              <h3 className="text-2xl font-black tracking-tighter text-emerald-400 leading-tight">
                {loading ? '...' : `₹${reportData.totalEarn}`}
              </h3>
            </div>
          </div>

        </div>

        {/* Offer Wise Breakdown Section */}
        <div className="space-y-6">
          <h3 className="text-xl font-black tracking-tight flex items-center gap-3 px-2 text-zinc-100">
            <span className="w-2 h-8 bg-emerald-500 rounded-full"></span>
            Offer Wise Breakdown
          </h3>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-zinc-500 text-sm font-semibold tracking-wide">Loading performance report...</p>
            </div>
          ) : reportData.offers.length === 0 ? (
            <div className="text-center py-16 bg-zinc-900/50 rounded-[2.5rem] border border-zinc-800 backdrop-blur-sm">
              <p className="text-zinc-400 text-sm font-semibold">No offer data available for the selected period.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pb-10">
              {reportData.offers.map((item, index) => (
                <div
                  key={item.id}
                  style={{ animationDelay: `${index * 80}ms` }}
                  className="group bg-zinc-900/90 backdrop-blur-md border border-zinc-800/80 rounded-[2.5rem] p-6 shadow-xl hover:border-emerald-500/40 transition-all duration-500 micro-interaction animate-spring-in"
                >
                  <div className="flex items-center gap-4 mb-6">
                    {renderLogo(item.logo, item.name)}
                    <div className="overflow-hidden">
                      <h4 className="font-black text-zinc-100 group-hover:text-emerald-400 transition-colors truncate">
                        {item.name || 'Offer Details'}
                      </h4>
                      <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest block mt-0.5">
                        Offer ID: #{item.id}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="bg-zinc-950/60 p-4 rounded-2xl border border-zinc-800/80">
                      <div className="flex items-center gap-2 text-emerald-400 mb-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                        </svg>
                        <span className="text-[10px] font-black uppercase">Clicks</span>
                      </div>
                      <p className="text-xl font-black text-zinc-100">{item.clicks}</p>
                    </div>

                    <div className="bg-zinc-950/60 p-4 rounded-2xl border border-zinc-800/80">
                      <div className="flex items-center gap-2 text-amber-400 mb-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        <span className="text-[10px] font-black uppercase">Conv</span>
                      </div>
                      <p className="text-xl font-black text-zinc-100">{item.conversions}</p>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate(`/reports/detailed/${item.id}`)}
                    className="w-full flex items-center justify-center gap-2 py-4 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-2xl font-bold text-sm shadow-lg shadow-emerald-500/10 active:scale-95 transition-all micro-interaction"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    View Detailed Report
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default Reports;